import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'

interface ActivityLogEntry {
  id: string
  user_id?: string | null
  user_name?: string | null
  action: string
  entity_type?: string | null
  entity_id?: string | null
  details?: Record<string, unknown> | null
  created_at: string
}

const PAGE_SIZE = 20

const toLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const getActionColor = (action: string) => {
  const lowered = action.toLowerCase()
  if (lowered.includes('created')) {
    return { color: '#15803D', background: '#F0FDF4' }
  }
  if (lowered.includes('suspend') || lowered.includes('delete')) {
    return { color: '#DC2626', background: '#FEF2F2' }
  }
  return { color: '#1D4ED8', background: '#EFF6FF' }
}

const formatDetails = (details?: Record<string, unknown> | null) => {
  if (!details) {
    return '-'
  }

  const entries = Object.entries(details)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${toLabel(key)}: ${String(value)}`)

  return entries.length > 0 ? entries.join(' · ') : '-'
}

export default function ActivityLogPage() {
  const colors = useColors()
  const [logs, setLogs] = useState<ActivityLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [entityFilter, setEntityFilter] = useState('all')

  const loadLogs = useCallback(async () => {
    setLoading(true)

    try {
      const response = await adminApi.getActivityLog({
        page,
        limit: PAGE_SIZE,
        entity_type: entityFilter === 'all' ? undefined : entityFilter,
      })
      const payload = response?.data ?? response
      const nextLogs = Array.isArray(payload?.logs) ? payload.logs : []
      setLogs(nextLogs)
      setTotal(Number(payload?.total || 0))
    } finally {
      setLoading(false)
    }
  }, [entityFilter, page])

  useEffect(() => {
    void loadLogs()
  }, [loadLogs])

  const { start, end } = useMemo(() => {
    if (total === 0) {
      return { start: 0, end: 0 }
    }
    return {
      start: (page - 1) * PAGE_SIZE + 1,
      end: Math.min(page * PAGE_SIZE, total),
    }
  }, [page, total])

  const canGoPrevious = page > 1
  const canGoNext = page * PAGE_SIZE < total

  return (
    <div>
      <div style={{ padding: '24px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="font-display" style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: colors.textPrimary }}>
          Activity Log
        </h2>
        <select
          value={entityFilter}
          onChange={(event) => {
            setEntityFilter(event.target.value)
            setPage(1)
          }}
          className="font-body"
          style={{
            height: '38px',
            borderRadius: '10px',
            border: `1.5px solid ${colors.border}`,
            padding: '0 12px',
            fontSize: '13px',
            color: colors.textPrimary,
          }}
        >
          {['all', 'order', 'agent', 'customer', 'partner', 'coupon'].map((entity) => (
            <option key={entity} value={entity}>
              {entity === 'all' ? 'All Entities' : toLabel(entity)}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          margin: '20px 32px',
          background: colors.bgCard,
          borderRadius: '16px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {['Time', 'User', 'Action', 'Details'].map((header) => (
                <th
                  key={header}
                  className="font-body"
                  style={{
                    padding: '14px 18px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'left',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ padding: '28px 18px' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    Loading activity...
                  </p>
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: '28px 18px' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    No activity logs found.
                  </p>
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const actionStyle = getActionColor(log.action)
                return (
                  <tr key={log.id} style={{ borderBottom: `0.5px solid ${colors.border}` }}>
                    <td className="font-body" style={{ padding: '14px 18px', fontSize: '12px', color: colors.textSecondary }}>
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </td>
                    <td className="font-body" style={{ padding: '14px 18px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      {log.user_name || 'System'}
                    </td>
                    <td style={{ padding: '14px 18px' }}>
                      <span
                        className="font-body"
                        style={{
                          background: actionStyle.background,
                          color: actionStyle.color,
                          borderRadius: '999px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      >
                        {toLabel(log.action)}
                      </span>
                    </td>
                    <td className="font-body" style={{ padding: '14px 18px', fontSize: '12px', color: colors.textSecondary }}>
                      {formatDetails(log.details)}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '0 32px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
          Showing {start}-{end} of {total} logs
        </p>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="font-body"
            disabled={!canGoPrevious}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: colors.textPrimary,
              background: colors.bgCard,
              cursor: canGoPrevious ? 'pointer' : 'not-allowed',
              opacity: canGoPrevious ? 1 : 0.4,
            }}
          >
            Prev
          </button>
          <button
            type="button"
            className="font-body"
            disabled={!canGoNext}
            onClick={() => setPage((current) => current + 1)}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: colors.textPrimary,
              background: colors.bgCard,
              cursor: canGoNext ? 'pointer' : 'not-allowed',
              opacity: canGoNext ? 1 : 0.4,
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
